import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface IntercomBootOptions {
  [key: string]: any;
  api_base?: string;
  app_id: string;
  user_id?: string;
  name?: string;
  email?: string;
  created_at?: number;
  intercom_user_jwt?: string;
}

declare global {
  interface Window {
    Intercom?: any;
    intercomSettings?: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  private isLoaded = false;
  private isBooted = false;
  private isLoading = false;

  public constructor() {
    // Script loading is deferred to boot() method to ensure it only loads for authenticated users
  }

  /**
   * Boot Intercom with user data
   */
  public boot(options: IntercomBootOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        console.warn('IntercomService: Window is undefined, cannot boot');
        reject(new Error('Window is undefined'));
        return;
      }

      if (!environment.intercomId) {
        console.warn('IntercomService: No Intercom ID configured');
        reject(new Error('No Intercom ID configured'));
        return;
      }

      if (this.isBooted) {
        console.info('IntercomService: Already booted, updating instead');
        // Strip JWT and system fields - only send user attributes to update()
        const { intercom_user_jwt, app_id, api_base, ...userOptions } = options;
        this.update(userOptions);
        resolve();
        return;
      }

      // Load Intercom script on first boot (deferred from constructor to ensure authenticated users only)
      if (!this.isLoaded && !this.isLoading) {
        this.isLoading = true;
        this.loadIntercomScript();
      }

      // If JWT is provided, set it in window.intercomSettings before booting
      if (options.intercom_user_jwt) {
        window.intercomSettings = window.intercomSettings || {};
        window.intercomSettings.intercom_user_jwt = options.intercom_user_jwt;
      }

      // Wait for script to load (check isLoaded flag to ensure real script loaded, not just stub)
      const checkLoaded = setInterval(() => {
        if (this.isLoaded && window.Intercom) {
          clearInterval(checkLoaded);
          clearTimeout(timeoutHandle);

          // Check if another concurrent boot() call already booted Intercom
          if (this.isBooted) {
            console.info('IntercomService: Already booted by concurrent call, updating instead');
            // Strip JWT and system fields - only send user attributes to update()
            const { intercom_user_jwt, app_id, api_base, ...userOptions } = options;
            this.update(userOptions);
            resolve();
            return;
          }

          // Set isBooted before calling boot() to prevent other intervals from booting
          this.isBooted = true;

          try {
            // Remove JWT from boot options as it's already in intercomSettings
            const { intercom_user_jwt, ...bootOptions } = options;

            const finalBootOptions = {
              api_base: environment.intercomApiBase,
              ...bootOptions,
            };

            window.Intercom('boot', finalBootOptions);

            // Force update to ensure user attributes are set
            // Handle update separately - don't reset isBooted if update fails
            try {
              window.Intercom('update', {
                user_id: bootOptions.user_id,
                name: bootOptions.name,
                email: bootOptions.email,
              });
            } catch (updateError) {
              console.warn('IntercomService: Update after boot failed', updateError);
              // Don't reset isBooted - Intercom is still booted successfully
            }

            resolve();
          } catch (error) {
            // Reset isBooted flag only if boot() itself failed
            this.isBooted = false;
            console.error('IntercomService: Boot failed', error);
            reject(error);
          }
        }
      }, 100);

      // Timeout after 10 seconds
      const timeoutHandle = setTimeout(() => {
        clearInterval(checkLoaded);
        if (!this.isBooted) {
          this.isLoading = false;
          const error = new Error('Intercom script failed to load (timeout or network error)');

          console.error('IntercomService: Script load timeout - check network, CSP, or ad blockers', error);
          reject(error);
        }
      }, 10000);
    });
  }

  /**
   * Update Intercom with new user data
   */
  public update(data?: Partial<IntercomBootOptions>): void {
    if (typeof window !== 'undefined' && window.Intercom && this.isBooted) {
      try {
        window.Intercom('update', data || {});
      } catch (error) {
        console.error('IntercomService: Update failed', error);
      }
    }
  }

  /**
   * Show the Intercom messenger
   */
  public show(): void {
    if (typeof window !== 'undefined' && window.Intercom && this.isBooted) {
      try {
        window.Intercom('show');
      } catch (error) {
        console.error('IntercomService: Show failed', error);
      }
    }
  }

  /**
   * Hide the Intercom messenger
   */
  public hide(): void {
    if (typeof window !== 'undefined' && window.Intercom && this.isBooted) {
      try {
        window.Intercom('hide');
      } catch (error) {
        console.error('IntercomService: Hide failed', error);
      }
    }
  }

  /**
   * Shutdown Intercom
   */
  public shutdown(): void {
    if (typeof window !== 'undefined') {
      // Clear JWT from window.intercomSettings to prevent credential leakage across sessions
      if (window.intercomSettings?.intercom_user_jwt) {
        delete window.intercomSettings.intercom_user_jwt;
      }

      if (window.Intercom && this.isBooted) {
        try {
          window.Intercom('shutdown');
          this.isBooted = false;
        } catch (error) {
          console.error('IntercomService: Shutdown failed', error);
        }
      }
    }
  }

  /**
   * Track an event in Intercom
   */
  public trackEvent(eventName: string, metadata?: Record<string, any>): void {
    if (typeof window !== 'undefined' && window.Intercom && this.isBooted) {
      try {
        window.Intercom('trackEvent', eventName, metadata);
      } catch (error) {
        console.error('IntercomService: Track event failed', error);
      }
    }
  }

  /**
   * Get the current boot status
   */
  public isIntercomBooted(): boolean {
    return this.isBooted;
  }

  /**
   * Load the Intercom script dynamically
   */
  private loadIntercomScript(): void {
    if (this.isLoaded || typeof window === 'undefined') {
      return;
    }

    // Initialize Intercom function
    this.initializeIntercomFunction();

    // Set global Intercom settings (without JWT - will be added in boot)
    window.intercomSettings = {
      api_base: environment.intercomApiBase,
      app_id: environment.intercomId,
    };

    if (environment.environment !== 'prod') {
      console.info('Intercom App ID:', environment.intercomId);
    }

    // Create and inject script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${environment.intercomId}`;

    script.onload = () => {
      this.isLoaded = true;
      this.isLoading = false;

      console.info('IntercomService: Script loaded successfully');
    };

    script.onerror = (error) => {
      this.isLoading = false;

      console.error('IntercomService: Failed to load script', error);
    };

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      // Fallback: append to document.head if no scripts exist or parent is missing
      try {
        (document.head || document.body).appendChild(script);
        console.warn('IntercomService: No existing scripts found, appending to document.head');
      } catch (error) {
        this.isLoading = false;
        console.error('IntercomService: Failed to append script to DOM', error);
      }
    }
  }

  /**
   * Initialize the Intercom function on the window object
   */
  private initializeIntercomFunction(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const w = window as any;
    const ic = w.Intercom;

    if (typeof ic === 'function') {
      ic('reattach_activator');
      ic('update', w.intercomSettings);
    } else {
      // Create Intercom stub function
      const i: any = (...args: any[]) => {
        i.c(args);
      };
      i.q = [];
      i.c = (args: any) => {
        i.q.push(args);
      };
      w.Intercom = i;
    }
  }
}
